import { clamp, smoothstep } from '@/core/rng';
import { CHUNK_SIZE } from './chunk';
import { STANDING_HALF } from './things';
import {
  GRAIN, MAX_DEPTH, STRAIN_CELL, branchKey, fitOf, hostOfKey, isOrdinary,
  mixAt, newStrainMix, strainOf,
  type StrainHost, type StrainKey, type StrainMix, type StrainTraits,
} from './strain';
import type { Region } from './region';
import type { StrainFieldSnapshot } from '@/sim/persistence/snapshot';

/**
 * Sparse plant-strain history on the 260 m lattice — which way the ground's
 * mixture has been pushed, and how far.
 *
 * The baseline underneath is never touched: `mixAt` is a pure function of
 * position and seed, and this store holds only the DIVERGENCE from it (law 2).
 * A world with an empty field is byte-identical to a world with no strain
 * history at all, which is what makes untouched ground free.
 *
 * ## Nothing reads this yet
 *
 * Commit 3 builds the store and its tests and stops there. Nothing under
 * `src/sim/` calls it, `applyStrains` does not exist yet, and the persistence
 * fingerprint is unchanged — `tools/strain.ts §0` is the gate that proves it
 * rather than the sentence that claims it, because two earlier designs claimed
 * a free stage and both were wrong about which of their edits ran inside
 * `Simulation.advance`.
 *
 * ## One slot per cell, and what that buys
 *
 * The key is `` `${gx},${gz}` `` — **one slot per cell, not per (cell, host).**
 * Pressing a different strain onto an already-tilted cell REPLACES the slot,
 * after decaying what was there to now, so overwriting costs you what you
 * built. That is a real strategic constraint and it is what turns a 17.9 MB
 * structural ceiling into a 0.87 MB one: a god shapes a valley, not every
 * layer of it at once.
 *
 * ## No god id, anywhere, ever
 *
 * `applyStrains` will recolour chunks from this store, so a hand written into
 * a cell is a hand that has reached something witnessable (law 10). This is
 * `EnvCell`'s own `pm`/`pb` argument verbatim: a number saying *this ground
 * has been pushed toward that strain* carries no cause at all. Whose hand it
 * was belongs to the Chronicle, which nothing in the world can read.
 */

// ---------------------------------------------------------------------------
// the constants, and which of them are measured
// ---------------------------------------------------------------------------

/**
 * Below this a tilt is noise and the cell is forgotten.
 *
 * **MEASURED**, by `tools/strain.ts §1i`, and deliberately NOT copied from the
 * neighbour. `EnvField.NEGLIGIBLE = 0.02` once ate a small persistent
 * influence outright and `aura.ts` carries the whole autopsy: a 0.0055 daily
 * instalment sat under that floor and was swept every half-hour for ever, so
 * the aura paid upkeep and changed nothing. A strain tilt is *precisely* a
 * small, durable, cumulative influence, so rule S1 says the floor goes below
 * the smallest tilt that changes anything.
 *
 * §1i builds 43 vegetated chunks through the real `buildChunk` at LOD 0. Run
 * three times this sitting it reads, identically every time, **p10 17, p50 73,
 * p90 347, max 368** plants. (The note here used to quote p10 16. The tool
 * prints 17; the load-bearing figure, p90, does reproduce, so the constant
 * stands and only that digit was wrong.) One plant flipping strain is
 * therefore **1/347 = 0.0029** in the busiest chunks and 1/73 = 0.0137 in the
 * median one. Rule S1 says take the smaller; this sits at 52% of it.
 *
 * **That denominator is `buildChunk`'s own, so it moves whenever vegetation
 * density moves** — and it was read against the working tree of a sprint that
 * is editing `chunk.ts` in another lane, so it will move again. So the floor is
 * NOT asserted against the quoted 347. `test/strain.ts` walks §1i's own scatter
 * through the real `buildChunk`, takes p90 from what it finds, and asserts
 * `NEGLIGIBLE_TILT < 1/p90` against that — it printed the same 43 chunks and
 * the same p90 347 as the tool when this landed, and it will follow the tool
 * wherever vegetation density goes. When it goes red it is this constant that
 * moves, never the assertion.
 *
 * **The other half of the joint check is still open.** The floor must also sit
 * below the aura's per-stamp delivery at `AURA_STAMP_DAYS = 7`, and that needs
 * `TILT_GAIN`, which is commit 5's. `tools/strain.ts §3` runs both together and
 * must do so before commit 6 ships the Anchor — the aura's bug was in the
 * *cadence*, not the rate, and it will not be caught by looking at one number.
 */
export const NEGLIGIBLE_TILT = 0.0015;

/**
 * Tilt is stored as a signed integer of thousandths. It saves ~10 B a row
 * against float serialisation and removes JSON float-length variance from the
 * snapshot.
 *
 * ### What this scale does NOT buy, and the autopsy is worth keeping
 *
 * This comment used to claim that "a clamped integer means
 * `JSON.stringify(Infinity) === null` cannot occur here … closed by
 * construction rather than by care." **It was false when it was written, and
 * the sentence protected the bug.** Quantising on write closes nothing on its
 * own, because every write path quantises a number it got from `valueAt`, and
 * `valueAt` was unbounded: `decay` is `v · 0.5^(dt/half)`, so a read at
 * `now < c.at` is `0.5` to a NEGATIVE power and grows without limit. A
 * snapshot restored with `at` in the far future — or any hand-edited file —
 * then went straight through `settle`/`burn`/`press` into `v`.
 *
 * `tools/strain.ts §1k` is that autopsy, kept runnable: it restores one cell at
 * `at: 1e12`, drives `settle`, `burn` and `press` at `now = 0`, and prints what
 * each stores and how many `null`s the encoded snapshot carries. Shipped it
 * reads 0.900 / 0.315 / 0.950 stored and **0 nulls** on every row. Undo the
 * bound — drop `bounded` from `valueAt` **and** put `clamp(next, −1, 1)` back
 * in `press`, which §1k's footer spells out — and the same table prints
 * `Infinity`, `Infinity`, `NaN` and **one `null` on each of the three write
 * rows**. (`clamp` is a min/max pair, so NaN fails both comparisons and passes
 * through unchanged.)
 *
 * What closes it is `valueAt` returning a **bounded** number, NaN included —
 * see its header. The quantisation is then what keeps the row small, which is
 * all it ever was.
 *
 * The resolution is 0.001, against §1i's smallest meaningful tilt of 0.0029,
 * so a meaningful change is three quantisation steps. That is thin but real;
 * if a later reading moves the plant counts down, this scale moves up.
 */
const TILT_SCALE = 1000;

/**
 * The smallest tilt the store will KEEP: the first quantum at or above
 * `NEGLIGIBLE_TILT`, which is 2 (0.002) at the shipped pair.
 *
 * The quantum (0.001) is finer than the floor (0.0015), so `q = ±1` is a value
 * that can be written and can never be read — `cellAt` and `blendAt` both
 * refuse it and the next `sweep` deletes it. Storing one is the aura's exact
 * failure shape: pay upkeep, change nothing.
 *
 * **MEASURED, and the probe is committed rather than remembered**:
 * `tools/strain.ts §1k` presses 1,200 m at one land point and prints, per
 * weight, how many cells the disc covers, how many the store keeps and how
 * many it refuses. Run this sitting it reads
 *
 *     weight 0.90 / 0.50 :  69 covered, 69 kept,  0 refused
 *     weight 0.20 / 0.05 :  69 covered, 61 kept,  8 refused
 *
 * — so the leak this rule closes was there only at the low weights a slow
 * accumulating cast uses, which is the one place it would have mattered. (The
 * note here used to quote those figures against a §1k that did not exist in
 * `tools/`. It does now, and these are what it prints.)
 *
 * So every write path drops a cell it cannot put above the floor, and the store
 * carries the invariant that **no cell is unreadable at its own `at`** — §1k's
 * `unreadable` and `swept at at` columns are both 0 in every row, and
 * `test/strain.ts` asserts the same thing over a fresh press. `size`,
 * `chunks()` and the snapshot then mean what they say.
 */
const MIN_STORED_Q = Math.ceil(NEGLIGIBLE_TILT * TILT_SCALE);

/**
 * How the tilt's half-life reads the grain of the land, as multiples of
 * `STANDING_HALF` (imported, not restated, so the two cannot drift).
 *
 * **STATUS: GUESSED**, and the probe that settles them is `tools/strain.ts §3`,
 * which needs a god casting at the measured rate — commit 5. What §1j *can*
 * price today is the clock, and it does:
 *
 *     25 world-years  =  1,140.6 real hours at 192x   (clock.ts:52, SHIPPED)
 *                     =     21.9 real hours at 10,000x  (D §4, canonical)
 *
 * Note the first column. The specification wrote 3,285 real hours at 192x for
 * the same 25 world-years; 9,125 world-days at 450 real seconds each is
 * 1,140.6 hours, and §1j prints the arithmetic. The 10,000x column is right.
 *
 * The criterion these must be picked against, once §3 can run: *a session's
 * work must still be there next session; an Age of neglect must erase it.* At
 * the canonical clock a session is hours, so the with-grain multiplier is
 * doing most of that work and is the one to distrust.
 */
const HALF_WITH_GRAIN = 2.0;
const HALF_AGAINST_GRAIN = 0.25;

/**
 * The half-width of the realised fit scale, used to decide how far *with* the
 * grain a tilt is running.
 *
 * **MEASURED**, `tools/strain.ts §1g`: realised fit across the island reads p05
 * 0.222, p50 0.483, p95 0.706 around `GRAIN.fitBase = 0.50`, so a strain at the
 * edge of what the island offers it is about 0.24 off the ordinary reading.
 * Blending over that span rather than branching on a sign keeps the half-life
 * continuous as `fitOf` crosses `fitBase` — a hard branch there would make the
 * decay rate jump the moment a god raised a ridge under it.
 */
const GRAIN_SPAN = 0.24;

/**
 * What a fire leaves of a tilt. Fire is the selection event in nature and it
 * runs whether or not anyone is watching, so this is the natural half of "what
 * moves without a god" (laws 11 and 16).
 *
 * **STATUS: GUESSED.** `tools/strain.ts §9` is the probe — god-free branch and
 * purity rates over 20 world-years — and it cannot run until the sim reads a
 * strain. The direction is not in doubt (a burn resets recruitment, so a tilt
 * built on standing growth loses most of itself); the magnitude is.
 */
const BURN_KEEP = 0.35;

// ---------------------------------------------------------------------------
// the two history gates (2026-08-19, the wake — WIRING.md's activation plan
// step 1, decided here and enforced by `history()`)
// ---------------------------------------------------------------------------

/**
 * A tilt at or above this counts as ESTABLISHED PRESENCE — the strain is what
 * this ground now mostly is, not a lean on it. Both gates read it.
 *
 * **STATUS: DESIGN NUMBER.** The composition math gives it meaning: at
 * v = 0.60 the pressed key holds ≥ 60% of the mixture wherever it stands
 * (`effectiveMixAt` moves the key's share at least as far as the tilt), so
 * "established" is a statement about what a walk through the stand meets,
 * not about the store's internals. `tools/strain.ts §9` re-derives it against
 * realised settle rates once a god can cast at the measured rate.
 */
export const SETTLE_THRESHOLD = 0.6;

/**
 * How long a tilt must HOLD at the threshold — untended, decaying — before it
 * has earned permanence. Eight world-years, and the asymmetry with the land
 * is the point rather than a side effect: for a MEDIAN-spread strain, a
 * with-grain tilt (half-life `2.0 × STANDING_HALF × spread` ≈ decades) loses
 * almost nothing in eight years and settles from one strong press, while a
 * hard against-grain tilt (× 0.25 → ~6 years) falls under the threshold
 * first and cannot settle unattended — work against the land needs a keeper
 * for as long as it is meant to last, which is law 17 priced in time rather
 * than in impulse, and law 19's dependence made structural. The calibration
 * is at the median deliberately: `spread` multiplies the half-life BY DESIGN
 * (it is the axis of holding on), so the stickiest strains can settle from
 * further against the grain than ordinary ones — that is their trait
 * expressing, not the gate leaking, and it is only learnable by acting,
 * exactly as the invisible axis promises. `press` refreshes `at`, so the
 * clock is "since the last hand", never "since the first".
 */
export const SETTLE_AFTER = 8 * 365 * 86400;

/**
 * Law 33's counterpart, demanded where `settle` was built: *a settled tilt is
 * un-settled by a large disturbance, never by neglect.* A burn that cuts a
 * settled cell below this puts it back on the clock — still present, still
 * strong, but mortal again. Above it, the establishment survives the fire.
 */
export const UNSETTLE_BELOW = 0.35;

/**
 * How long a cell must have been SETTLED before isolation can make a daughter
 * of it — the speciation gate's clock. Twenty world-years of established,
 * held presence: a branch is a fact about a lineage, not about a season.
 */
export const BRANCH_AFTER = 20 * 365 * 86400;

/**
 * How far from its own hearth a settled strain must stand to count as
 * ISOLATED — the peripatric margin. 0.75 × `GRAIN.reach` is the outer band of
 * the kernel, where the baseline mixture still names the strain but barely:
 * ground it holds out there is ground something HELD for it, which is exactly
 * the "displaced population, cut off, drifting" story `branchKey` encodes.
 * Deliberately inside `reach` — the baseline can only ever carry a strain
 * within its own kernel, so a margin outside it would make the gate
 * unreachable until a transplant verb exists. When one does, true exile
 * branches arrive with it and this constant is re-argued.
 */
export const BRANCH_MARGIN = 0.75 * GRAIN.reach;

/**
 * Fire's own press — natural selection writing the field (TRAITS.md: "burn is
 * the natural selection event"). Where a burn finds a mixture whose dominant
 * is NOT its least-flammable member, the ground tilts a little toward the
 * member the fire favours. Repeated fire over the same ground converges the
 * tilt toward `SELECT_TOWARD` (press semantics: `1 − (1−w)ⁿ` of the way).
 * The target sits ABOVE `SETTLE_THRESHOLD` on purpose: a press converges
 * from below and never reaches its own target, so a ceiling AT the threshold
 * would let a fire regime approach establishment for ever and never cross —
 * at 0.7, roughly a dozen fires over the same ground cross 0.6 and the
 * regime can genuinely settle. One fire is an event; a dozen are a climate.
 *
 * **STATUS: DESIGN NUMBERS**, measured by the god-free worldgen run this wake
 * ends in (`tools/strain.ts §9` prices the realised rates).
 */
export const SELECT_TOWARD = 0.7;
export const SELECT_WEIGHT = 0.2;
/** The exposure edge a member needs over the dominant before fire prefers it. */
export const SELECT_MIN_EDGE = 0.06;
/** Selection cannot USURP: ground held toward a DIFFERENT key stronger than
 *  this is a fact fire may cut (`burn`) but not overwrite — the one-slot
 *  replace semantics would otherwise let a 0.14 nudge erase three times its
 *  weight of someone's standing work. Ground already tilted toward the
 *  fire-fit member itself keeps converging: fire agreeing with fire is the
 *  regime, not an overwrite. */
export const SELECT_MAX_HELD = 0.3;
/** Members under this share are not present enough to inherit the ground. */
const SELECT_MIN_SHARE = 0.05;

// ---------------------------------------------------------------------------
// the record
// ---------------------------------------------------------------------------

/**
 * One tilted cell. **NO ARRAY IN THIS RECORD.** Every store here snapshots by
 * spread, and a spread copies an array BY REFERENCE — a flat record makes that
 * silent corruption impossible rather than merely avoided. The internal shape
 * and the snapshot shape are the same shape on purpose, so `toSnapshot` is a
 * copy and not a translation.
 */
interface StrainCell {
  gx: number; gz: number;
  /** The strain the ground is moving TOWARD (v > 0) or AWAY FROM (v < 0). */
  k: StrainKey;
  /** Signed tilt × TILT_SCALE, integer, clamped. Quantised on WRITE only. */
  v: number;
  /** World-seconds. Decay runs one-shot from here — never accumulated. */
  at: number;
  /** Held long enough to stop decaying (law 4). NEVER `at: Infinity`. */
  settled?: boolean;
  /** When it settled. Needed by the branching gate; not derivable. */
  since?: number;
}

/** A signed tilt as read out of the store. */
export interface TiltMix { n: number; key: StrainKey[]; v: Float32Array }

/** A tilt buffer with room for `cap` keys. Four is the bilinear stencil. */
export const newTiltMix = (cap = 4): TiltMix =>
  ({ n: 0, key: new Array<StrainKey>(cap).fill(''), v: new Float32Array(cap) });

const decay = (v: number, dt: number, half: number): number =>
  v * Math.pow(0.5, dt / half);

/**
 * Clamp to [−1, 1], **NaN included**. `core/rng`'s `clamp` is `v < a ? a : v >
 * b ? b : v` — a min/max pair, and NaN fails both comparisons, so it passes
 * through unchanged and reaches the snapshot as `null`. Every number this
 * store hands out or writes goes through here instead.
 */
const bounded = (v: number): number =>
  Number.isNaN(v) ? 0 : v > 1 ? 1 : v < -1 ? -1 : v;

/** Every chunk key a 260 m cell touches. 260 does not divide 256, so 1–4. */
const cellChunks = (gx: number, gz: number, into: Set<string>): void => {
  const x0 = Math.floor(gx * STRAIN_CELL / CHUNK_SIZE);
  const x1 = Math.floor(((gx + 1) * STRAIN_CELL - 1) / CHUNK_SIZE);
  const z0 = Math.floor(gz * STRAIN_CELL / CHUNK_SIZE);
  const z1 = Math.floor(((gz + 1) * STRAIN_CELL - 1) / CHUNK_SIZE);
  for (let cz = z0; cz <= z1; cz++) for (let cx = x0; cx <= x1; cx++) into.add(`${cx},${cz}`);
};

export class StrainField {
  private readonly cells = new Map<string, StrainCell>();
  private version_ = 0;
  private readonly region: Region;

  /**
   * The field needs the Region because its decay reads the ground: a tilt
   * running WITH what `fitOf` already says of a cell is long-lived and one
   * running against it bleeds out. That coupling is the whole of "what moves
   * without a god" — raise a ridge, let a burn run, let the sere shift the
   * stand, and the local optimum moves under yesterday's work.
   */
  constructor(region: Region) { this.region = region; }

  // -------------------------------------------------------------------------
  // decay
  // -------------------------------------------------------------------------

  /**
   * This cell's half-life in world-seconds. A pure function of **the cell's own
   * site, the strain's `spread`, and the SIGN of the tilt — never of the
   * current value.**
   *
   * That last clause is the one that stops a silent law-7 break. Decay is
   * computed one-shot from `at` and never accumulated, so
   * `advance(a,b) + advance(b,c) === advance(a,c)` exactly. A half-life that
   * read the decayed value would make the rate depend on how often you looked,
   * and **nothing would fail a test** — the store would simply be a slightly
   * different store in a session that rendered more frames.
   */
  private halfOf(c: StrainCell): number {
    if (isOrdinary(c.k)) return STANDING_HALF;
    const s = strainOf(this.region.seed, c.k);
    const site = this.region.siteAt((c.gx + 0.5) * STRAIN_CELL, (c.gz + 0.5) * STRAIN_CELL);
    const sign = c.v > 0 ? 1 : c.v < 0 ? -1 : 0;
    // +1 fully with the grain, −1 fully against it, 0 where the land is
    // indifferent. Blended rather than branched — see GRAIN_SPAN.
    const g = clamp(sign * (fitOf(s, site) - GRAIN.fitBase) / GRAIN_SPAN, -1, 1);
    const mult = HALF_AGAINST_GRAIN
      + (HALF_WITH_GRAIN - HALF_AGAINST_GRAIN) * (g + 1) / 2;
    return STANDING_HALF * s.traits.spread * mult;
  }

  /**
   * The cell's tilt decayed to `now`, as a float in [−1, 1] — **and the bound
   * is enforced here rather than promised in a docstring.**
   *
   * Two things had to be closed and both were open (see `TILT_SCALE`):
   *
   * 1. **Decay never runs backwards.** `decay` is `v · 0.5^(dt/half)`, so a
   *    read at `now < c.at` raises 0.5 to a negative power and AMPLIFIES the
   *    tilt without limit — ×542 at one probed cell, and Infinity from a
   *    hand-edited `at`. That is not a time step, it is a malformed `at` or a
   *    caller reading before the write; either way the honest answer is the
   *    value as written, so `dt ≤ 0` returns the stored tilt undecayed. This
   *    also catches `now` being NaN, which `Math.max(0, NaN)` would not.
   * 2. **The bound is NaN-safe.** `0 × Infinity` is NaN, `clamp` passes NaN
   *    through, and `JSON.stringify(NaN)` is `null` exactly as
   *    `JSON.stringify(Infinity)` is. This repository has already paid twice
   *    for a `null` where a number belongs.
   *
   * Every write path (`press`, `burn`, `settle`) quantises what this returns,
   * so bounding it here is what makes an out-of-range or non-finite `v`
   * unreachable — by construction, which is what the old comment claimed
   * without doing.
   */
  private valueAt(c: StrainCell, now: number): number {
    const raw = bounded(c.v / TILT_SCALE);
    if (c.settled) return raw;              // held; law 4
    const dt = now - c.at;
    if (!(dt > 0)) return raw;
    return bounded(decay(raw, dt, this.halfOf(c)));
  }

  // -------------------------------------------------------------------------
  // reads
  // -------------------------------------------------------------------------

  /** The raw tilt of the cell containing (wx, wz), decayed. Null where none. */
  cellAt(wx: number, wz: number, now: number): { key: StrainKey; v: number } | null {
    const gx = Math.floor(wx / STRAIN_CELL), gz = Math.floor(wz / STRAIN_CELL);
    const c = this.cells.get(`${gx},${gz}`);
    if (!c) return null;
    const v = this.valueAt(c, now);
    return Math.abs(v) < NEGLIGIBLE_TILT ? null : { key: c.k, v };
  }

  /**
   * The tilt at a point, **bilinear across the four surrounding cell centres**,
   * accumulated per key. Fills `out` in place; nothing allocates.
   *
   * The soft read is not a nicety. A 260 m store read by snapping would draw a
   * hard-edged quadrilateral on the hillside wherever a god had worked, and
   * `keepFraction`'s own header records that exact artifact shipping once: *"a
   * hard-edged quadrilateral of bare ground in the middle distance… a property
   * of the streamer showing up as a shape in the land."* **That mistake must
   * not be made a second time by a different system**, so the store hands out
   * the soft read and no caller has to remember to do it.
   *
   * Interpolating the weights rather than the values is what keeps it
   * continuous when two adjacent cells carry different strains: each key's
   * total rises and falls smoothly across the stencil instead of one key
   * winning a square outright.
   */
  blendAt(wx: number, wz: number, now: number, out: TiltMix): void {
    out.n = 0;
    if (this.cells.size === 0) return;      // untouched ground costs nothing
    const fx = wx / STRAIN_CELL - 0.5, fz = wz / STRAIN_CELL - 0.5;
    const gx = Math.floor(fx), gz = Math.floor(fz);
    const tx = fx - gx, tz = fz - gz;
    const cap = out.key.length;
    for (let j = 0; j <= 1; j++) for (let i = 0; i <= 1; i++) {
      const w = (i === 0 ? 1 - tx : tx) * (j === 0 ? 1 - tz : tz);
      if (w <= 0) continue;
      const c = this.cells.get(`${gx + i},${gz + j}`);
      if (!c) continue;
      const v = this.valueAt(c, now) * w;
      if (v === 0) continue;
      let slot = -1;
      for (let s = 0; s < out.n; s++) if (out.key[s] === c.k) { slot = s; break; }
      if (slot < 0) {
        if (out.n >= cap) continue;
        slot = out.n++;
        out.key[slot] = c.k;
        out.v[slot] = 0;
      }
      out.v[slot] += v;
    }
    // Drop anything the interpolation left under the floor, so a reader never
    // sees a key carrying a tilt too small to have changed a single plant.
    let n = 0;
    for (let s = 0; s < out.n; s++) {
      if (Math.abs(out.v[s]) < NEGLIGIBLE_TILT) continue;
      out.key[n] = out.key[s]; out.v[n] = out.v[s]; n++;
    }
    out.n = n;
  }

  // -------------------------------------------------------------------------
  // writes
  // -------------------------------------------------------------------------

  /**
   * Press the ground toward (or away from) a strain: a soft disc centred on
   * (wx, wz), moving each cell's tilt a fraction `weight` of the way to
   * `toward`.
   *
   * **It is a MOVE TOWARD A COORDINATE, not an increment**, and that is the
   * single most likely implementation slip in the whole design. `EnvField.stamp`
   * *accumulates* and then clamps; copying its shape here would make the tenth
   * identical cast worth as much as the first, which is law 25 inverted. Moving
   * toward a coordinate makes the marginal gain fall off geometrically —
   * `gain₁₀ / gain₁ = (1 − weight)⁹` — **by construction, not by a tuned
   * curve.** `test/strain.ts` asserts it directly.
   *
   * Pressing a DIFFERENT strain onto an already-tilted cell replaces the slot:
   * what was there is decayed to `at` and then discarded, so overwriting costs
   * you what you built.
   *
   * A cell the press cannot lift to `MIN_STORED_Q` is **deleted, not written**:
   * a tilt under the floor is one no reader can see and the next sweep would
   * take anyway, and storing it is the aura's failure shape exactly.
   *
   * Returns the chunk keys whose appearance changed — the keys of cells that
   * were VISIBLE before or after, never of a cell that was under the floor at
   * both ends and therefore changed nothing anybody can see.
   *
   * `at` must be a real number: a write timestamped NaN would put a `null` in
   * the snapshot as surely as an infinite `v` would.
   */
  press(
    wx: number, wz: number, radius: number,
    key: StrainKey, toward: number, weight: number, at: number,
  ): string[] {
    const dirty = new Set<string>();
    if (!(radius > 0) || !(weight > 0) || !Number.isFinite(at)) return [];
    const t = clamp(toward, -1, 1);
    const w0 = clamp(weight, 0, 1);
    let changed = false;
    const g0x = Math.floor((wx - radius) / STRAIN_CELL), g1x = Math.floor((wx + radius) / STRAIN_CELL);
    const g0z = Math.floor((wz - radius) / STRAIN_CELL), g1z = Math.floor((wz + radius) / STRAIN_CELL);
    for (let gz = g0z; gz <= g1z; gz++) for (let gx = g0x; gx <= g1x; gx++) {
      const cx = (gx + 0.5) * STRAIN_CELL, cz = (gz + 0.5) * STRAIN_CELL;
      // EnvField.stamp's own falloff shape, so two soft discs on the same
      // ground have the same edge and a god cannot tell them apart by feel.
      const fall = 1 - smoothstep(0.35, 1, Math.hypot(cx - wx, cz - wz) / radius);
      if (fall <= 0) continue;
      const k = `${gx},${gz}`;
      const prev = this.cells.get(k);
      // One `valueAt` per cell: it reads the site and rebuilds the strain, so
      // it is the expensive call in this loop.
      const prevV = prev ? this.valueAt(prev, at) : 0;
      const seen = Math.abs(prevV) >= NEGLIGIBLE_TILT;
      const cur = prev && prev.k === key ? prevV : 0;
      const next = cur + (t - cur) * w0 * fall;
      const q = Math.round(bounded(next) * TILT_SCALE);
      if (Math.abs(q) < MIN_STORED_Q) {
        if (prev) { this.cells.delete(k); changed = true; }
        if (seen) cellChunks(gx, gz, dirty);   // a visible tilt just went out
        continue;
      }
      this.cells.set(k, {
        gx, gz, k: key, v: q, at,
        ...(prev && prev.k === key && prev.settled
          ? { settled: true, since: prev.since ?? at }
          : {}),
      });
      changed = true;
      cellChunks(gx, gz, dirty);
    }
    if (changed) this.version_++;
    return [...dirty];
  }

  /**
   * Fire resets recruitment. One call at the site where `FireSim` already calls
   * `sere.recordBurn` (commit 6), and it is the natural half of "what moves
   * without a god": a range a god built on standing growth is cut by the fire
   * that growth made likelier, and every hop of that chain already exists.
   *
   * A cell the fire takes below the floor is **forgotten, not zeroed**. That
   * was a real leak: `{ ...c }` carried `settled` forward, and `sweep` skipped
   * settled cells unconditionally, so a settled cell burned enough times
   * reached `v: 0` and then lived for ever — unreadable, unsweepable,
   * serialised into every save and still naming its chunks dirty. Twenty burns
   * over a settled disc left one such cell behind and `sweep(10,000 y)` could
   * not touch it.
   */
  burn(wx: number, wz: number, radius: number, at: number): string[] {
    const dirty = new Set<string>();
    if (!(radius > 0) || !Number.isFinite(at)) return [];
    let changed = false;
    const g0x = Math.floor((wx - radius) / STRAIN_CELL), g1x = Math.floor((wx + radius) / STRAIN_CELL);
    const g0z = Math.floor((wz - radius) / STRAIN_CELL), g1z = Math.floor((wz + radius) / STRAIN_CELL);
    for (let gz = g0z; gz <= g1z; gz++) for (let gx = g0x; gx <= g1x; gx++) {
      const k = `${gx},${gz}`;
      const c = this.cells.get(k);
      if (!c) continue;
      const cx = (gx + 0.5) * STRAIN_CELL, cz = (gz + 0.5) * STRAIN_CELL;
      if (Math.hypot(cx - wx, cz - wz) > radius) continue;
      const before = this.valueAt(c, at);
      const seen = Math.abs(before) >= NEGLIGIBLE_TILT;
      const v = Math.round(before * BURN_KEEP * TILT_SCALE);
      if (Math.abs(v) < MIN_STORED_Q) this.cells.delete(k);
      else if (c.settled && Math.abs(v) < UNSETTLE_BELOW * TILT_SCALE) {
        // law 33's counterpart: a large disturbance un-settles what neglect
        // never could. The tilt stands, cut, and is mortal again.
        this.cells.set(k, { gx: c.gx, gz: c.gz, k: c.k, v, at });
      } else this.cells.set(k, { ...c, v, at });
      changed = true;
      if (seen) cellChunks(gx, gz, dirty);
    }
    if (changed) this.version_++;
    return [...dirty];
  }

  /**
   * Hold a cell: it stops decaying, and it remembers when it stopped.
   *
   * The setter exists in commit 3 so the snapshot shape is complete and
   * round-trips; **the gate that decides when a cell has earned it is history
   * and belongs to commit 9**, along with the counterpart law 33 demands — a
   * settled tilt is un-settled by a large disturbance, never by neglect.
   *
   * A cell that has already decayed under the floor holds nothing, so settling
   * it would freeze an invisible row into every future save. It is forgotten
   * instead. That is not neglect erasing a held tilt (law 4): there was
   * nothing left to hold at the moment `settle` was called.
   */
  settle(gx: number, gz: number, at: number): void {
    const c = this.cells.get(`${gx},${gz}`);
    if (!c || c.settled || !Number.isFinite(at)) return;
    const v = Math.round(this.valueAt(c, at) * TILT_SCALE);
    if (Math.abs(v) < MIN_STORED_Q) this.cells.delete(`${gx},${gz}`);
    else this.cells.set(`${gx},${gz}`, { ...c, v, at, settled: true, since: at });
    this.version_++;
  }

  /**
   * THE HISTORY GATES — what earns `settle`, and what calls `branchKey`. The
   * two design decisions the activation plan said came first, made and
   * enforced in one deterministic walk (2026-08-19, the wake).
   *
   * **Settling** is persistence: a tilt that has held at `SETTLE_THRESHOLD`
   * for `SETTLE_AFTER` without a hand on it (`press` refreshes `at`) has
   * proved it belongs, and the store stops charging it decay — law 4, with
   * the with/against-grain asymmetry doing the moral work (see the constants).
   *
   * **Branching** is isolation: a cell SETTLED toward a strain for
   * `BRANCH_AFTER`, standing past `BRANCH_MARGIN` from that strain's own
   * hearth, becomes the birthplace of a daughter — the cell's key is
   * rewritten to `branchKey(parent, gx, gz)` with the tilt kept and the
   * settle clock restarted. The key IS the phylogeny (`strain.ts`), so
   * nothing else is stored; at most one daughter per (parent, cell) by
   * construction; depth past `MAX_DEPTH` refuses (an over-deep key would
   * *read* as ordinary — minting one would erase the very establishment that
   * earned it). Only a POSITIVE tilt branches: an absence has no lineage.
   *
   * Deterministic and cadence-free: every predicate reads (cell, now) alone,
   * so calling this on any absolute grid — or twice — reaches the same state
   * (`advance(a,b)+advance(b,c) === advance(a,c)` holds because a gate that
   * fired at b would have fired anywhere in (a,c] and firing is idempotent).
   * The caller owns the cadence and the consequences: returned cells name
   * what changed so the sim can dirty chunks and, one day, let the world say
   * *the wood here is not what it was*.
   */
  history(now: number): {
    settled: Array<{ gx: number; gz: number; key: StrainKey }>;
    branched: Array<{ gx: number; gz: number; from: StrainKey; to: StrainKey }>;
    dirty: string[];
  } {
    const settled: Array<{ gx: number; gz: number; key: StrainKey }> = [];
    const branched: Array<{ gx: number; gz: number; from: StrainKey; to: StrainKey }> = [];
    const dirty = new Set<string>();
    if (!Number.isFinite(now)) return { settled, branched, dirty: [] };
    for (const [k, c] of this.cells) {
      if (!c.settled) {
        // the settle gate: held at threshold, unattended, long enough
        if (now - c.at < SETTLE_AFTER) continue;
        const v = this.valueAt(c, now);
        if (Math.abs(v) < SETTLE_THRESHOLD) continue;
        this.cells.set(k, {
          gx: c.gx, gz: c.gz, k: c.k, v: Math.round(v * TILT_SCALE), at: now,
          settled: true, since: now,
        });
        settled.push({ gx: c.gx, gz: c.gz, key: c.k });
        cellChunks(c.gx, c.gz, dirty);
        continue;
      }
      // the branch gate: settled toward, long enough, far enough from home
      if (c.v <= 0 || isOrdinary(c.k)) continue;
      if (now - (c.since ?? c.at) < BRANCH_AFTER) continue;
      if (c.k.split('>').length - 1 >= MAX_DEPTH) continue;
      const s = strainOf(this.region.seed, c.k);
      const cx = (c.gx + 0.5) * STRAIN_CELL, cz = (c.gz + 0.5) * STRAIN_CELL;
      if (Math.hypot(cx - s.hx, cz - s.hz) < BRANCH_MARGIN) continue;
      const to = branchKey(c.k, c.gx, c.gz);
      this.cells.set(k, {
        gx: c.gx, gz: c.gz, k: to, v: c.v, at: now, settled: true, since: now,
      });
      branched.push({ gx: c.gx, gz: c.gz, from: c.k, to });
      cellChunks(c.gx, c.gz, dirty);
    }
    if (settled.length || branched.length) this.version_++;
    return { settled, branched, dirty: [...dirty] };
  }

  /**
   * FIRE'S OWN PRESS — natural selection, so the field can be written by
   * history that has no god in it (TRAITS.md: "burn is the natural selection
   * event"; law 16: natural history is real history). Call it where the fire
   * scars out, after `burn()` has cut what stood.
   *
   * Reads the BASELINE mixture at the point for the given host; if its
   * dominant member is not its least-flammable member (by at least
   * `SELECT_MIN_EDGE` of exposure, among members holding `SELECT_MIN_SHARE`),
   * the ground is pressed a little toward the member the fire favours —
   * ordinary included: burning a flammable strain's country tilts it back
   * toward the ordinary kind, which is what recovery looks like from above.
   * Ground already strongly held (|tilt| > `SELECT_MAX_HELD`) is skipped:
   * fire may CUT an establishment (`burn`) but a nudge does not overwrite one
   * — the one-slot-per-cell replace semantics would otherwise let a 0.14
   * survivor press erase three times its weight of standing history.
   *
   * Returns the dirty chunk keys, exactly as `press` does.
   */
  naturalSelect(host: StrainHost, wx: number, wz: number, radius: number, at: number): string[] {
    if (!(radius > 0) || !Number.isFinite(at)) return [];
    mixAt(this.region, host, wx, wz, selectMix);
    if (selectMix.n < 2) return [];
    // the dominant, and the least-flammable member that genuinely stands here
    let bestI = -1, bestExp = Infinity, domExp = 1;
    for (let i = 0; i < selectMix.n; i++) {
      if (selectMix.share[i] < SELECT_MIN_SHARE) continue;
      const exp = strainOf(this.region.seed, selectMix.key[i]).traits.exposure;
      if (i === 0) domExp = exp;
      if (exp < bestExp) { bestExp = exp; bestI = i; }
    }
    if (bestI <= 0 || domExp - bestExp < SELECT_MIN_EDGE) return [];
    const cur = this.cellAt(wx, wz, at);
    if (cur && cur.key !== selectMix.key[bestI] && Math.abs(cur.v) > SELECT_MAX_HELD) return [];
    return this.press(wx, wz, radius, selectMix.key[bestI], SELECT_TOWARD, SELECT_WEIGHT, at);
  }

  /**
   * Forget every cell no reader can see; return their chunk keys.
   *
   * The rule is "what nothing can read", not "what has decayed", and the
   * difference is the whole of the fix: this used to skip `c.settled`
   * unconditionally, so a settled cell written to nothing by a burn was
   * immortal. A settled cell does not decay, so it can only fall under the
   * floor by an explicit write — sweeping it is collecting an empty row, never
   * neglect taking a held tilt.
   */
  sweep(now: number): string[] {
    const dirty = new Set<string>();
    for (const [k, c] of this.cells) {
      if (Math.abs(this.valueAt(c, now)) >= NEGLIGIBLE_TILT) continue;
      this.cells.delete(k);
      cellChunks(c.gx, c.gz, dirty);
    }
    if (dirty.size > 0) this.version_++;
    return [...dirty];
  }

  /** All chunk keys currently carrying a tilt. */
  chunks(): string[] {
    const keys = new Set<string>();
    for (const c of this.cells.values()) cellChunks(c.gx, c.gz, keys);
    return [...keys];
  }

  get size(): number { return this.cells.size; }

  /**
   * Bumps whenever the field's contents change. Not history — a cache tag, the
   * same contract `EnvField.version` already has and for the same stated
   * reason: *a world restored from a save recomputes the same numbers its
   * donor recomputes.* `HabitatSampler`'s key joins this in commit 8.
   */
  get version(): number { return this.version_; }

  /** Every tilted cell, copied out in insertion order. Shares nothing. */
  toSnapshot(): StrainFieldSnapshot {
    return { cells: [...this.cells.values()].map((c) => ({ ...c })) };
  }

  /**
   * Rebuild a field from a snapshot. Shares no object with the source, which
   * `test/strain.ts` checks by MUTATING THE DONOR and re-reading the copy —
   * the only form of that assertion that has ever caught anything here.
   *
   * **Every field is re-derived rather than trusted, and that now means every
   * field.** It used to mean `v` alone, which is how a hand-edited file could
   * still reach the world:
   *
   * - `gx`/`gz` are integerised. A fractional index produced a Map key no read
   *   path could ever hit — `cellAt` floors to integers — so the row was
   *   unreachable for ever while `toSnapshot` still reported it and `chunks()`
   *   still named its chunks dirty. Probed: `gx: 1.5` restored, read back
   *   `null`, and named four chunks.
   * - `since` is checked for finiteness like `at` and `v`. `c.since ?? c.at`
   *   passed `Infinity` straight through, and `JSON.stringify(Infinity)` is
   *   `null` — the same trap one field over.
   * - A row whose tilt lands under `MIN_STORED_Q` is dropped. This build never
   *   writes one (every write path drops it), so such a row can only come from
   *   a hand or another build, and keeping it would break the store's own
   *   invariant on the way in.
   *
   * `at` is only checked for finiteness, and deliberately: a far-future or
   * negative `at` is now harmless, because `valueAt` refuses to run decay
   * backwards. That was the precondition for the `Infinity` write, and it is
   * closed there rather than guessed at here — this function has no clock and
   * cannot know what "future" means.
   *
   * A key this build cannot rebuild is not dropped — `strainOf` is total, so it
   * simply reads as the host's ordinary strain.
   */
  static fromSnapshot(snap: StrainFieldSnapshot, region: Region): StrainField {
    const field = new StrainField(region);
    for (const c of snap.cells ?? []) {
      if (!Number.isFinite(c.gx) || !Number.isFinite(c.gz) || !Number.isFinite(c.at)) continue;
      if (typeof c.k !== 'string' || c.k.length === 0) continue;
      const gx = Math.round(c.gx), gz = Math.round(c.gz);
      const v = Math.round(clamp(Number(c.v) || 0, -TILT_SCALE, TILT_SCALE));
      if (Math.abs(v) < MIN_STORED_Q) continue;
      const at = c.at;
      const since = Number.isFinite(c.since) ? (c.since as number) : at;
      field.cells.set(`${gx},${gz}`, {
        gx, gz, k: c.k, v, at,
        ...(c.settled ? { settled: true, since } : {}),
      });
    }
    return field;
  }
}

// module scratch for naturalSelect — same discipline as strain.ts's own:
// nothing survives a call, so purity in the only sense that matters holds
const selectMix = newStrainMix(GRAIN.mixMax + 1);

// ---------------------------------------------------------------------------
// the effective read — CURRENT GROUND = BASELINE + TILT (law 2, per point)
// ---------------------------------------------------------------------------

/**
 * The mixture at a point AS THE WORLD NOW STANDS: the pure baseline
 * (`mixAt`), moved by whatever tilt the field holds there. This is the ONE
 * composition — the sim's trait readers, the renderer's phenotype source and
 * the card all go through it, so what the ecology runs on, what the eye sees
 * and what the card says can never be three different mixtures.
 *
 * A tilt `v` toward key K moves K's share `v` of the way to 1 (and, negative,
 * `|v|` of the way to 0), with every other member rescaled to keep the sum at
 * 1 — so `SETTLE_THRESHOLD = 0.6` literally means "at least 60% of this
 * ground". Tilts of OTHER hosts are ignored (one store carries all nine
 * hosts' keys; a grazer tilt says nothing about broadleaf ground). Multiple
 * keys from the bilinear read apply strongest-first, key-order on ties, so
 * the result cannot depend on Map traversal in a later refactor.
 *
 * Null or empty field short-circuits to the baseline — untouched ground costs
 * exactly what it cost before the field existed.
 */
export function effectiveMixAt(
  region: Region, field: StrainField | null, host: StrainHost,
  wx: number, wz: number, now: number,
  out: StrainMix, tilts?: TiltMix,
): void {
  mixAt(region, host, wx, wz, out);
  if (!field || field.size === 0) return;
  const t = tilts ?? scratchTilts;
  field.blendAt(wx, wz, now, t);
  if (t.n === 0) return;
  // strongest first, key order on ties — a deterministic application order
  const order: number[] = [];
  for (let i = 0; i < t.n; i++) if (hostOfKey(t.key[i]) === host) order.push(i);
  if (order.length === 0) return;
  order.sort((a, b) => (Math.abs(t.v[b]) - Math.abs(t.v[a])) || (t.key[a] < t.key[b] ? -1 : 1));

  for (const i of order) {
    const key = t.key[i];
    const v = clamp(t.v[i], -1, 1);
    let slot = -1;
    for (let s = 0; s < out.n; s++) if (out.key[s] === key) { slot = s; break; }
    if (slot < 0) {
      if (v <= 0) continue;              // pushing away what is not here
      if (out.n < out.key.length) {
        slot = out.n++;
      } else {
        // full: the smallest share yields its slot (deterministic — shares
        // are sorted descending after every application below)
        slot = out.n - 1;
      }
      out.key[slot] = key;
      out.share[slot] = 0;
    }
    const s0 = out.share[slot];
    const s1 = v > 0 ? s0 + v * (1 - s0) : s0 * (1 + v);
    // every other member rescales to keep the sum at 1
    const restBefore = 1 - s0;
    const factor = restBefore > 1e-9 ? (1 - s1) / restBefore : 0;
    for (let s = 0; s < out.n; s++) {
      out.share[s] = s === slot ? s1 : out.share[s] * factor;
    }
    // re-sort descending (insertion-sized n; ties on key for determinism)
    for (let a = 1; a < out.n; a++) {
      for (let b = a; b > 0 && (out.share[b] > out.share[b - 1]
        || (out.share[b] === out.share[b - 1] && out.key[b] < out.key[b - 1])); b--) {
        const sk = out.key[b]; out.key[b] = out.key[b - 1]; out.key[b - 1] = sk;
        const sv = out.share[b]; out.share[b] = out.share[b - 1]; out.share[b - 1] = sv;
      }
    }
  }
  // drop members ground down to nothing, so `n` means what it says
  let n = 0;
  for (let s = 0; s < out.n; s++) {
    if (out.share[s] <= 1e-6) continue;
    out.key[n] = out.key[s]; out.share[n] = out.share[s]; n++;
  }
  out.n = Math.max(1, n);
}
const scratchTilts = newTiltMix();

/**
 * The mixture-weighted traits of one host's ground, as it now stands — the
 * number the ecology actually multiplies. `out` is filled in place; the
 * ordinary members contribute exactly 1 per axis, so untouched ordinary
 * ground reads all-ones to the bit and every reader's neutral path is a
 * multiply-by-one.
 */
export function effectiveTraitsAt(
  region: Region, field: StrainField | null, host: StrainHost,
  wx: number, wz: number, now: number,
  out: StrainTraits, mix?: StrainMix, tilts?: TiltMix,
): void {
  const m = mix ?? scratchEffMix;
  effectiveMixAt(region, field, host, wx, wz, now, m, tilts);
  let y = 0, t = 0, e = 0, s = 0, total = 0;
  for (let i = 0; i < m.n; i++) {
    const share = m.share[i];
    if (share <= 0) continue;
    const tr = strainOf(region.seed, m.key[i]).traits;
    y += share * tr.yield; t += share * tr.tolerance;
    e += share * tr.exposure; s += share * tr.spread;
    total += share;
  }
  if (total <= 0) { out.yield = 1; out.tolerance = 1; out.exposure = 1; out.spread = 1; return; }
  out.yield = y / total; out.tolerance = t / total;
  out.exposure = e / total; out.spread = s / total;
}
const scratchEffMix = newStrainMix(GRAIN.mixMax + 1);
